import React, { useEffect, useRef } from "react";
import CoursesCardSquare from "./Cards/CoursesCardSquare";
import HorizontalScroll from "./HorizontalScroll";
import { DisplayCourse } from "@/hooks/useCourses";
import { useCompletedLessons } from "@/hooks/useCompletedLessons";
import { rankCoursesByRecency } from "@/lib/courseRanking";

interface CourseHighlightProps {
  courses: DisplayCourse[];
  onClick: (courseId: number) => void;
}

// How many courses to surface as suggestions in the empty state (iOS shows 3-4).
const MAX_SUGGESTED_COURSES = 4;

const CourseHighlight: React.FC<CourseHighlightProps> = ({
  courses,
  onClick,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { completedLessons } = useCompletedLessons();

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollLeft = 0;
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [courses]);

  // iOS parity (client-native CourseHighlight.tsx): show every incomplete
  // course, sorted by completed-lesson recency then progress, then dedupe by
  // courseId. Same ranking as the Home current-course fallback — shared via
  // rankCoursesByRecency so the two surfaces can't drift.
  const incompleteCourses = rankCoursesByRecency(courses, completedLessons);

  const seenCourseIds = new Set<number>();
  const displayCourses = incompleteCourses.filter((course) => {
    if (seenCourseIds.has(course.courseId)) {
      return false;
    }
    seenCourseIds.add(course.courseId);
    return true;
  });

  // Empty state (iOS CourseHighlight: rendered when displayCourses.length === 0).
  // Nothing is "in progress" — a fresh user (or one who has finished everything)
  // would otherwise see a blank "Pick up where you left off" area. Show the
  // iOS empty-state card plus a few SUGGESTED courses, each with its own
  // prominent Continue CTA (CoursesCardSquare renders the per-card button), so
  // the user can start a course immediately.
  if (displayCourses.length === 0) {
    // Suggest from the full (already filtered/sorted upstream) course list,
    // deduped by courseId, capped to match iOS (3-4 cards).
    const suggestedSeen = new Set<number>();
    const dedupedCourses = courses.filter((course) => {
      if (suggestedSeen.has(course.courseId)) {
        return false;
      }
      suggestedSeen.add(course.courseId);
      return true;
    });
    // Prefer courses the user can still make progress on; fall back to the full
    // list (e.g. everything completed) so suggestions are never empty here.
    const startableCourses = dedupedCourses.filter(
      (course) => course.progress !== 100,
    );
    const suggestedCourses = (
      startableCourses.length > 0 ? startableCourses : dedupedCourses
    ).slice(0, MAX_SUGGESTED_COURSES);

    return (
      <HorizontalScroll ref={scrollContainerRef} className="w-full min-w-0">
        <div className="flex flex-row gap-4 h-auto min-w-max">
          <div
            className="flex flex-col items-center justify-center gap-4 shrink-0 bg-white rounded-[32px] p-4 shadow-[0_1px_8px_rgba(0,0,0,0.05)]"
            style={{
              minWidth: "var(--large-card-width)",
              maxWidth: "var(--large-card-width)",
              height: "var(--large-card-width)",
            }}
          >
            <div style={{ fontSize: "var(--base-font-size-6xlarge)" }}>📚</div>
            <div className="flex flex-col items-center gap-2 px-2">
              <div className="font-bold text-2xl text-center text-[var(--color-grey)]">
                Pick up where you left off
              </div>
              <div className="text-base text-center text-[var(--color-grey)]">
                Choose a suggested course below to get started
              </div>
            </div>
          </div>
          {suggestedCourses.map((course) => (
            <CoursesCardSquare
              key={course.courseId}
              emoji={course.icon || "📚"}
              title={course.name}
              subtitle={course.description}
              level={course.level}
              progress={course.progress}
              chapters={`${course.totalChapters} chapters`}
              onClick={() => onClick(course.courseId)}
            />
          ))}
        </div>
      </HorizontalScroll>
    );
  }

  return (
    <HorizontalScroll ref={scrollContainerRef} className="w-full min-w-0">
      <div className="flex flex-row gap-4 h-auto min-w-max">
        {displayCourses.map((course) => (
          <CoursesCardSquare
            key={course.courseId}
            emoji={course.icon || "📚"}
            title={course.name}
            subtitle={course.description}
            level={course.level}
            progress={course.progress}
            chapters={`${course.totalChapters} chapters`}
            onClick={() => onClick(course.courseId)}
          />
        ))}
      </div>
    </HorizontalScroll>
  );
};

export default CourseHighlight;
